import { inject, Injectable } from '@angular/core';
import { DialogService } from '../../shared/services/dialog.service';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { map, Observable, of, tap } from 'rxjs';
import { RESELLER_PACKAGE_FORM } from '../models/form/reseller-package-form';
import { FilterFieldMap, ResellerPackage, ResellerPackageDTO } from '../models';
import { HttpParams } from '@angular/common/http';
import { PayloadRequest, PayloadResponse } from '../models/payload.model';
import { ApiService } from '../../shared/services';
import { trimFormValues, formatIsoDateToItalian } from '../../shared/utils';
import { DEFAULT_START_DATE, DEFAULT_END_DATE } from '../constants/date.constants';

@Injectable({
  providedIn: 'root'
})
export class ResellerPackageStoreService {
  private apiService = inject(ApiService);
  private dialogService = inject(DialogService);
  constructor(
    private router: Router
  ) { }

  createResellerPackageFormGroup(): FormGroup {
    const formBuilder = new FormBuilder();
    const formControls: { [key: string]: FormControl } = {};

    Object.keys(RESELLER_PACKAGE_FORM).forEach(key => {
      const field = RESELLER_PACKAGE_FORM[key];
      formControls[key] = new FormControl({ value: field.value, disabled: !!field.disabled }, field.validators);
    });

    return formBuilder.group(formControls);
  }

  loadResellerPackageData(id: number, form: FormGroup): Observable<any> {
    return this.getResellerPackageById(id).pipe(
      tap(resPack => {
        form.patchValue(resPack);
      })
    );
  }

  navigateToList(): void {
    this.router.navigate(['/main/magazzino']);
  }

  getResellerPackageFormField(): Observable<any> {
    return of(RESELLER_PACKAGE_FORM);
  }

  getResellersPackageList(filter: FilterFieldMap,
    pageNumber?: number,
    pageSize?: number
  ): Observable<PayloadResponse<ResellerPackage[]>> {
    try {
      let params = this.setParams(filter);
  
      if (pageNumber) params = params.set('pageNumber', pageNumber);
      if (pageSize) params = params.set('pageSize', pageSize);
      return this.apiService.get('resellers-package', { params: params });
      
    } catch (error) {
        this.dialogService.openDialogError('Errore', 'La data di fine deve essere maggiore della data di inizio.');
        return of();
    }
  }

  getResellerPackageListByResellerId(resellerId: number): Observable<PayloadResponse<ResellerPackage[]>> {
    let params = new HttpParams();
    params = params.set('resellerId', resellerId);
    return this.apiService.get('resellers-package', { params: params });
  }

  countItems(): Observable<{ activeItems: number; inactiveItems: number }> {
    return this.apiService.get('resellers-package/count-status').pipe(
      map(payload => payload.data)
    );
  }

  getResellerList(): Observable<{ id: number; name: string }[]> {
    return this.apiService.get('resellers-package/reseller-list').pipe(
      map(payload => payload.data)
    );
  }

  getCourseList(): Observable<{ id: number; name: string }[]> {
    return this.apiService.get('resellers-package/course-list').pipe(
      map(payload => payload.data)
    );
  }

  getResellerPackageById(resPackId: number): Observable<any> {
    return this.apiService.get(`resellers-package/${resPackId}`).pipe(
      map(resPack => {
        return {
          ...resPack.data,
          resellerId: resPack.data.reseller.resellerId,
          courseId: resPack.data.course.courseId,
          creationDate: formatIsoDateToItalian(resPack.data.creationDate),
        }
      })
    );
  }


  onSubmitForm(formResellerPackage: FormGroup): Observable<PayloadResponse<ResellerPackage>> {

    trimFormValues(formResellerPackage);

    if (formResellerPackage.invalid) {

      formResellerPackage.markAllAsTouched();

      this.dialogService.openDialogInfoNoRedirect('Errore', 'Compila tutti i campi obbligatori.');

      return of();
    }

    const resPack = this.convertTypeForm(formResellerPackage);
    return this.saveResellerPackage(resPack).pipe(
        tap(() => this.showDialogSuccess())
      );
  }

  private saveResellerPackage(resPack: ResellerPackageDTO): Observable<PayloadResponse<ResellerPackage>> {
    const payload = new PayloadRequest<ResellerPackageDTO>({ data: resPack });
    return this.apiService.post('resellers-package/create', payload);
  }

  private updateResellerPackage(resPack: ResellerPackageDTO): Observable<PayloadResponse<ResellerPackage>> {
    const payload = new PayloadRequest<ResellerPackageDTO>({ data: resPack });
    return this.apiService.patch(`resellers-package/update`, payload);
  }

  private showDialogSuccess(): void {
    this.dialogService.openDialogAlert('Operazione completata', 'Le licenze assegnate al rivenditore sono state create con successo', () => {
      this.router.navigate(['/main/magazzino']);
    });
  }

  convertTypeForm(form: FormGroup): ResellerPackageDTO {
    const resPack: ResellerPackageDTO = {
      resellerPackageId: form.value.resellerPackageId,
      resellerId: Number(form.value.resellerId),
      courseId: Number(form.value.courseId),
      initialAmount: form.value.initialAmount,
      orderType: form.value.orderType,
    }
    return resPack;
  }

  downloadResellerPackageLicenses(filter: FilterFieldMap): Observable<any> {
    let params = this.setParams(filter);
    return this.apiService.get('resellers-package/reseller-csv', {
       params: params,
       responseType: 'blob',
       observe: 'response',
      },
    );
  }

   deleteResellerPackage(resellerLicenseId: number): Observable<void> {
    return this.apiService.delete(`resellers-package/delete/${resellerLicenseId}`);
  }

  private setParams(filter: FilterFieldMap): HttpParams {
    let params = new HttpParams();
    if (filter) {
      if (filter['reseller'].value) params = params.set('resellerId', filter['reseller'].value);
      if (filter['course'].value) params = params.set('courseId', filter['course'].value);
      if (filter['isActive'].value) params = params.set('isActive', filter['isActive'].value);
    }
    const startDate = filter['startDate']?.value || DEFAULT_START_DATE;
    const endDate = filter['endDate']?.value || DEFAULT_END_DATE;

    if (startDate > endDate) {
        throw new Error('La data di fine deve essere maggiore della data di inizio.');
      }

    params = params.set('startDate', startDate);
    params = params.set('endDate', endDate);

    return params;
  }
}
